import { Box, CircularProgress, Typography } from "@mui/material";
import { UserInfoCard } from "../../components/UserInfo/UserInfoCard";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useEffect, useState } from "react";
import { getUserById } from "../../services/usersList.service";
import type { IUser } from "../../interfaces";
import { ROUTES } from "../../router/constants";

export function UserInfo() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user: loggedUser } = useSelector((state: RootState) => state.userInfo);

  const [viewUser, setViewUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const roleUpper = String(loggedUser?.role || "").toUpperCase();
    const isUserRole = roleUpper === "USER";

    // Usuários do tipo USER não podem acessar a tela de UserInfo (nem o próprio registro)
    if (isUserRole) {
      navigate(ROUTES.USER_PROFILE, { replace: true });
      return;
    }

    // Se não há id ou o id é o do usuário logado, mostramos o próprio usuário
    if (!id || id === loggedUser.id) {
      setViewUser(loggedUser);
      setError(null);
      setLoading(false);
      return;
    }

    // Acessar perfil de outro usuário só é permitido para ADMIN, OWNER e DOCTOR_OWNER
    const allowedRoles = ["ADMIN", "OWNER", "DOCTOR_OWNER"] as const;
    const canViewOthers = allowedRoles.includes(roleUpper as (typeof allowedRoles)[number]);

    if (!canViewOthers) {
      // Redireciona para o próprio perfil se não tiver permissão para ver outros usuários
      navigate(ROUTES.USER_PROFILE, { replace: true });
      return;
    }

    let ignore = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const result = await getUserById(id!);
        if (!ignore) {
          if (result) {
            setViewUser(result);
          } else {
            setError("Usuário não encontrado.");
          }
        }
      } catch (e) {
        if (!ignore) {
          setError("Não foi possível carregar o usuário. Tente novamente.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [id, loggedUser, navigate]);

  return (
    <Box 
      width="100%" 
      padding={4}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      {loading && (
        <CircularProgress />
      )}

      {!loading && error && (
        <Typography color="error">{error}</Typography>
      )}

      {!loading && !error && viewUser && (
        <UserInfoCard userOverride={viewUser} />
      )}
    </Box>
  )
}
